import React, { useState, useEffect } from 'react';
import { formatDateOnly, formatDateTime } from '../utils/dateUtils';
import { updateClient, loadClients } from '../utils/storage';
import { getFileUrl, apiFetch } from '../utils/apiUtils';

const TasksInlineSection = ({ client, currentUser }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('timeline');

  // Cargar tareas del cliente desde el servidor
  useEffect(() => {
    const loadClientTasks = async () => {
      try {
        setLoading(true);
        
        let clientTasks = [];
        
        // Primero, buscar en las juntas del cliente (datos locales)
        if (client.meetings && Array.isArray(client.meetings)) {
          client.meetings.forEach(meeting => {
            if (meeting.tasks && Array.isArray(meeting.tasks)) {
              meeting.tasks.forEach(task => {
                clientTasks.push({
                  ...task,
                  clientId: client.id,
                  clientName: client.name,
                  meetingDate: meeting.date,
                  meetingType: meeting.meetingType,
                  meetingId: meeting.id,
                  priority: task.priority || 'media',
                  status: task.status || (task.completed ? 'completada' : 'pendiente'),
                  cell: task.cell || client.cell || 'C1GA'
                });
              });
            }
          });
        }
        
        // Luego, buscar tareas adicionales del servidor que pertenezcan a las juntas de este cliente
        try {
          const meetingIds = client.meetings ? client.meetings.map(m => m.id).filter(id => id) : [];
          
          if (meetingIds.length > 0) {
            // Cargar todas las tareas del servidor
            const tasksResponse = await apiFetch('/tasks');
          
            if (tasksResponse.ok) {
              const allServerTasks = await tasksResponse.json();
              
              // Filtrar solo las tareas que pertenecen a las juntas de este cliente
              const clientServerTasks = allServerTasks.filter(task => 
                meetingIds.includes(task.meetingId)
              );
              
              // Agregar tareas del servidor que no estén ya en los datos locales
              clientServerTasks.forEach(serverTask => {
                const existsInLocal = clientTasks.some(localTask => 
                  localTask.id === serverTask.id || 
                  (localTask.meetingId === serverTask.meetingId && localTask.description === serverTask.description)
                );
                
                if (!existsInLocal) {
                  // Encontrar la junta correspondiente para obtener información adicional
                  const correspondingMeeting = client.meetings.find(m => m.id === serverTask.meetingId);
                  
                  clientTasks.push({
                    ...serverTask,
                    clientId: client.id,
                    clientName: client.name,
                    meetingDate: correspondingMeeting ? correspondingMeeting.date : (serverTask.createdAt || new Date().toISOString()),
                    meetingType: correspondingMeeting ? correspondingMeeting.meetingType : 'Tarea del sistema',
                    meetingId: serverTask.meetingId,
                    priority: serverTask.priority || 'media',
                    status: serverTask.completed ? 'completada' : 'pendiente',
                    cell: serverTask.cell || client.cell || 'C1GA'
                  });
                }
              });
            }
          }
        } catch (serverError) {
          console.log('Error al cargar tareas del servidor, usando solo datos locales:', serverError);
        }

        // Ordenar tareas por fecha (más antiguas primero)
        clientTasks.sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt) : new Date(a.meetingDate);
          const dateB = b.createdAt ? new Date(b.createdAt) : new Date(b.meetingDate);
          return dateA - dateB;
        });

        setTasks(clientTasks);

      } catch (error) {
        console.error('Error al cargar tareas del cliente:', error);
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };

    if (client?.id) {
      loadClientTasks();
    }
  }, [client]);

  const handleTaskCompletion = async (taskId, meetingId, isCompleted, comment) => {
    try {
      // Actualizar la tarea en el servidor
      const response = await apiFetch(`/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          completed: isCompleted ? 1 : 0,
          completionComment: comment,
          completedAt: isCompleted ? new Date().toISOString() : null,
          completedBy: isCompleted ? currentUser?.name || 'Usuario' : null
        })
      });

      if (!response.ok) {
        throw new Error('Error al actualizar la tarea en el servidor');
      }

      // Actualizar el estado local
      setTasks(prevTasks => 
        prevTasks.map(task => 
          task.id === taskId 
            ? { 
                ...task, 
                completed: isCompleted, 
                completionComment: comment,
                completedAt: isCompleted ? new Date().toISOString() : null,
                completedBy: isCompleted ? currentUser?.name || 'Usuario' : null
              } 
            : task
        )
      );
      
    } catch (error) {
      console.error('Error updating task:', error);
      alert('Error al actualizar la tarea: ' + error.message);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgente': return 'bg-red-100 text-red-800 border-red-200';
      case 'alta': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'media': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'baja': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityEmoji = (priority) => {
    switch (priority) {
      case 'urgente': return '🔴';
      case 'alta': return '🟠';
      case 'media': return '🟡';
      case 'baja': return '🟢';
      default: return '🟡';
    }
  };

  // Función para calcular el estado real de la tarea basado en fechas
  const getTaskRealStatus = (task) => {
    if (task.completed || task.status === 'completada') return { text: '✅ Completada', type: 'completed' };
    if (task.status === 'cancelada') return { text: '❌ Cancelada', type: 'cancelled' };
    if (task.status === 'en_progreso') return { text: '🔄 En Progreso', type: 'in_progress' };

    const now = new Date();
    const createdDate = task.createdAt ? new Date(task.createdAt) : new Date(task.meetingDate);
    const dueDate = task.dueDate ? new Date(task.dueDate) : null;
    
    const nowDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const createdDateNormalized = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
    const dueDateNormalized = dueDate ? new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate()) : null;
    
    const daysSinceCreated = Math.floor((nowDate - createdDateNormalized) / (1000 * 60 * 60 * 24));
    
    if (dueDateNormalized) {
      const daysUntilDue = Math.floor((dueDateNormalized - nowDate) / (1000 * 60 * 60 * 24));
      
      if (daysUntilDue < 0) {
        const daysOverdue = Math.abs(daysUntilDue);
        if (daysOverdue === 1) {
          return { text: '🔴 Vencida (1 día de retraso)', type: 'overdue_critical' };
        } else if (daysOverdue <= 3) {
          return { text: `🔴 Vencida (${daysOverdue} días de retraso)`, type: 'overdue_critical' };
        } else if (daysOverdue <= 7) {
          return { text: `🔴 CRÍTICA (${daysOverdue} días de retraso)`, type: 'overdue_critical' };
        } else {
          return { text: `🔴 URGENTE (${daysOverdue} días de retraso)`, type: 'overdue_critical' };
        }
      } else if (daysUntilDue === 0) {
        return { text: '🟡 Vence Hoy', type: 'due_today' };
      } else if (daysUntilDue === 1) {
        return { text: '🟡 Vence Mañana', type: 'due_soon' };
      }
    }
    
    if (daysSinceCreated === 0) {
      return { text: '🟢 Nueva (Hoy)', type: 'new_today' };
    } else if (daysSinceCreated === 1) {
      return { text: '🟡 1 día', type: 'one_day' };
    } else if (daysSinceCreated <= 3) {
      return { text: `🟡 ${daysSinceCreated} días`, type: 'few_days' };
    } else if (daysSinceCreated <= 7) {
      return { text: `🟠 ${daysSinceCreated} días (Revisar)`, type: 'week_old' };
    } else if (daysSinceCreated <= 14) {
      return { text: `🔴 ${daysSinceCreated} días (Atención)`, type: 'two_weeks' };
    } else {
      return { text: `🔴 ${daysSinceCreated} días (URGENTE)`, type: 'very_old' };
    }
  };

  // Función para obtener el color del estado real
  const getRealStatusColor = (statusType) => {
    switch (statusType) {
      case 'completed': return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled': return 'bg-red-100 text-red-800 border-red-200';
      case 'in_progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'new_today': return 'bg-green-100 text-green-800 border-green-200';
      case 'one_day': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'few_days': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'week_old': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'two_weeks': return 'bg-red-100 text-red-800 border-red-200';
      case 'very_old': return 'bg-red-100 text-red-800 border-red-200';
      case 'due_today': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'due_soon': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'overdue_critical': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 mb-8">
        <div className="flex items-center space-x-3 mb-6">
          <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Tareas del Cliente</h3>
        </div>
        <div className="text-center py-8">
          <div className="text-4xl mb-4">🔄</div>
          <p className="text-gray-500">Cargando tareas del cliente...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-8">
      {/* Header */}
      <div className="p-6 sm:p-8 border-b border-gray-200">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="p-3 bg-gradient-to-br from-[#A8D08D] to-[#8bc34a] rounded-xl">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
              </svg>
            </div>
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-[#0061A8]">Tareas del Cliente</h3>
              <p className="text-xs sm:text-sm text-gray-600 mt-1 flex flex-wrap gap-x-2">
                <span>Total: {tasks.length}</span>
                <span>•</span>
                <span>Completadas: {tasks.filter(t => t.completed).length}</span>
                <span>•</span>
                <span>Pendientes: {tasks.filter(t => !t.completed).length}</span>
              </p>
            </div>
          </div>
        </div>

        {/* Pestañas de navegación */}
        <div className="flex space-x-1 mt-6 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveTab('timeline')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'timeline'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📅 Timeline
          </button>
          <button
            onClick={() => setActiveTab('cards')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'cards'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            🗂️ Tarjetas
          </button>
          <button
            onClick={() => setActiveTab('table')}
            className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
              activeTab === 'table'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            📊 Tabla
          </button>
        </div>
      </div>

      {/* Contenido */}
      <div className="p-6 sm:p-8">
        {tasks.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">📝</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No hay tareas registradas</h3>
            <p className="text-gray-500">Este cliente no tiene tareas asignadas aún.</p>
          </div>
        ) : (
          <>
            {/* Vista Timeline */}
            {activeTab === 'timeline' && (
              <div className="space-y-4">
                <div className="relative">
                  {/* Línea vertical del timeline */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-300"></div>
                  
                  {tasks.map((task, index) => (
                    <div key={task.id || index} className="relative flex items-start space-x-4 pb-8">
                      {/* Punto del timeline */}
                      <div className={`relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                        task.completed 
                          ? 'bg-green-100 border-green-500 text-green-600' 
                          : 'bg-blue-100 border-blue-500 text-blue-600'
                      }`}>
                        <span className="text-xs font-bold">{index + 1}</span>
                      </div>
                      
                      {/* Contenido de la tarea */}
                      <div className="flex-1 min-w-0 bg-gray-50 border border-gray-200 rounded-lg p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-sm font-semibold text-gray-900 mb-1">
                              {task.title || task.description}
                            </h4>
                            <p className="text-xs text-gray-500 mb-2">
                              {formatDateOnly(task.createdAt || task.meetingDate)} • {task.meetingType || 'Tarea del sistema'}
                            </p>
                            
                            {/* Estado y prioridad */}
                            <div className="flex flex-wrap gap-2 mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                                {getPriorityEmoji(task.priority)} {task.priority || 'Media'}
                              </span>
                              {(() => {
                                const realStatus = getTaskRealStatus(task);
                                return (
                                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                                    {realStatus.text}
                                  </span>
                                );
                              })()}
                            </div>
                            
                            {/* Información básica */}
                            <div className="text-xs text-gray-600 space-y-1">
                              <div><span className="font-medium">Asignado a:</span> {task.assignedTo || 'Sin asignar'}</div>
                              <div><span className="font-medium">Fecha límite:</span> {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}</div>
                              {task.cell && <div><span className="font-medium">Célula:</span> {task.cell}</div>}
                            </div>
                          </div>
                          
                          {/* Checkbox de completado */}
                          <label className="flex items-center space-x-2 ml-4">
                            <input
                              type="checkbox"
                              checked={task.completed || false}
                              onChange={(e) => handleTaskCompletion(task.id, task.meetingId, e.target.checked, task.completionComment || '')}
                              className="h-4 w-4 text-green-600 rounded focus:ring-green-500"
                            />
                            <span className="text-xs text-gray-700">Realizada</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Vista Tarjetas */}
            {activeTab === 'cards' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {tasks.map((task, index) => (
                  <div key={task.id || index} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <div className="w-6 h-6 bg-blue-100 text-blue-800 rounded-full flex items-center justify-center font-bold text-xs">
                          {index + 1}
                        </div>
                        <h4 className="text-sm font-semibold text-gray-900 truncate">
                          {task.title || task.description}
                        </h4>
                      </div>
                      <label className="flex items-center space-x-1">
                        <input
                          type="checkbox"
                          checked={task.completed || false}
                          onChange={(e) => handleTaskCompletion(task.id, task.meetingId, e.target.checked, task.completionComment || '')}
                          className="h-3 w-3 text-green-600 rounded focus:ring-green-500"
                        />
                        <span className="text-xs text-gray-700">✓</span>
                      </label>
                    </div>
                    
                    <div className="space-y-2 text-xs text-gray-600">
                      <div><span className="font-medium">Fecha:</span> {formatDateOnly(task.createdAt || task.meetingDate)}</div>
                      <div><span className="font-medium">Asignado a:</span> {task.assignedTo || 'Sin asignar'}</div>
                      <div><span className="font-medium">Fecha límite:</span> {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}</div>
                      {task.cell && <div><span className="font-medium">Célula:</span> {task.cell}</div>}
                    </div>
                    
                    <div className="flex flex-wrap gap-1 mt-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                        {getPriorityEmoji(task.priority)} {task.priority || 'Media'}
                      </span>
                      {(() => {
                        const realStatus = getTaskRealStatus(task);
                        return (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                            {realStatus.text}
                          </span>
                        );
                      })()}
                    </div>
                    
                    {/* Indicadores de archivos */}
                    {(task.evidenceFile || task.audioFile || task.evidenceLink) && (
                      <div className="flex space-x-2 mt-2">
                        {task.evidenceFile && <span className="text-xs text-blue-600">🖼️</span>}
                        {task.audioFile && <span className="text-xs text-green-600">🎵</span>}
                        {task.evidenceLink && <span className="text-xs text-purple-600">🔗</span>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Vista Tabla */}
            {activeTab === 'table' && (
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tarea</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asignado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha Límite</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prioridad</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Archivos</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Completada</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {tasks.map((task, index) => (
                      <tr key={task.id || index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{index + 1}</td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate" title={task.title || task.description}>
                          {task.title || task.description}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{task.assignedTo || 'Sin asignar'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {task.dueDate ? formatDateOnly(task.dueDate) : 'Sin fecha'}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                            {getPriorityEmoji(task.priority)} {task.priority || 'Media'}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          {(() => {
                            const realStatus = getTaskRealStatus(task);
                            return (
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getRealStatusColor(realStatus.type)}`}>
                                {realStatus.text}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex space-x-1">
                            {task.evidenceFile && <span className="text-blue-600" title="Imagen">🖼️</span>}
                            {task.audioFile && <span className="text-green-600" title="Audio">🎵</span>}
                            {task.evidenceLink && <span className="text-purple-600" title="Enlace">🔗</span>}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={task.completed || false}
                              onChange={(e) => handleTaskCompletion(task.id, task.meetingId, e.target.checked, task.completionComment || '')}
                              className="h-4 w-4 text-green-600 rounded focus:ring-green-500"
                            />
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TasksInlineSection;
